import { addMinutes, differenceInMinutes, format } from "date-fns";
import i18next from "i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export const STEP_MINUTES = 30;

export interface WindowValue {
  start: Date | null;
  end: Date | null;
}

interface UseWindowStepperArgs {
  shiftStart: Date;
  shiftEnd: Date;
  // Minimum window length, in minutes (raw config value before the 30-min floor).
  minMinutes: number;
  // Current window. When the parent seeds or switches selection, the hook
  // adopts the new value.
  value: WindowValue;
  // Maximum window length, in minutes.
  maxMinutes?: number;
  // Fired on every accepted step with the resulting window.
  onChange: (start: Date, end: Date) => void;
  // When false, all steppers report disabled and no caption is produced.
  enabled?: boolean;
}

export interface WindowStepperState {
  start: Date | null;
  end: Date | null;
  canStartMinus: boolean;
  canStartPlus: boolean;
  canEndMinus: boolean;
  canEndPlus: boolean;
  caption: string | null;
  stepStart: (delta: number) => boolean;
  stepEnd: (delta: number) => boolean;
}

// Owns the local start/end window state and enforces the shared validation rules
// (within shift bounds, >= effective minimum, strictly shorter than the full
// shift). Refs mirror state so press-and-hold repeats read the latest value
// without waiting for a re-render.
export function useWindowStepper({
  shiftStart,
  shiftEnd,
  minMinutes,
  value,
  maxMinutes,
  onChange,
  enabled = true,
}: UseWindowStepperArgs): WindowStepperState {
  const totalMinutes = useMemo(
    () => differenceInMinutes(shiftEnd, shiftStart),
    [shiftStart, shiftEnd]
  );
  const effectiveMinMinutes = useMemo(
    () => Math.max(minMinutes, STEP_MINUTES),
    [minMinutes]
  );

  const [start, setStart] = useState<Date | null>(value.start);
  const [end, setEnd] = useState<Date | null>(value.end);
  const startRef = useRef<Date | null>(value.start);
  const endRef = useRef<Date | null>(value.end);

  const externalStart = value.start ? value.start.getTime() : null;
  const externalEnd = value.end ? value.end.getTime() : null;

  // Adopt external window changes (seeding on selection, switching worker slot).
  useEffect(() => {
    if (externalStart === null) {
      startRef.current = null;
      setStart(null);
      return;
    }
    if (!startRef.current || startRef.current.getTime() !== externalStart) {
      const next = new Date(externalStart);
      startRef.current = next;
      setStart(next);
    }
  }, [externalStart]);

  useEffect(() => {
    if (externalEnd === null) {
      endRef.current = null;
      setEnd(null);
      return;
    }
    if (!endRef.current || endRef.current.getTime() !== externalEnd) {
      const next = new Date(externalEnd);
      endRef.current = next;
      setEnd(next);
    }
  }, [externalEnd]);

  const isValidWindow = useCallback(
    (candidateStart: Date, candidateEnd: Date) => {
      if (candidateStart < shiftStart) {
        return false;
      }
      if (candidateEnd > shiftEnd) {
        return false;
      }
      const duration = differenceInMinutes(candidateEnd, candidateStart);
      if (duration < effectiveMinMinutes) {
        return false;
      }
      const maxAllowedMinutes = maxMinutes ?? totalMinutes;
      if (duration > maxAllowedMinutes) {
        return false;
      }
      return true;
    },
    [shiftStart, shiftEnd, effectiveMinMinutes, maxMinutes, totalMinutes]
  );

  const stepStart = useCallback(
    (delta: number) => {
      if (!startRef.current || !endRef.current) {
        return false;
      }
      const next = addMinutes(startRef.current, delta);
      if (!isValidWindow(next, endRef.current)) {
        return false;
      }
      startRef.current = next;
      setStart(next);
      onChange(next, endRef.current);
      return true;
    },
    [isValidWindow, onChange]
  );

  const stepEnd = useCallback(
    (delta: number) => {
      if (!startRef.current || !endRef.current) {
        return false;
      }
      const next = addMinutes(endRef.current, delta);
      if (!isValidWindow(startRef.current, next)) {
        return false;
      }
      endRef.current = next;
      setEnd(next);
      onChange(startRef.current, next);
      return true;
    },
    [isValidWindow, onChange]
  );

  const derived = useMemo(() => {
    if (!enabled || !start || !end) {
      return {
        canStartMinus: false,
        canStartPlus: false,
        canEndMinus: false,
        canEndPlus: false,
        caption: null as string | null,
      };
    }
    const canStartMinus = isValidWindow(addMinutes(start, -STEP_MINUTES), end);
    const canStartPlus = isValidWindow(addMinutes(start, STEP_MINUTES), end);
    const canEndMinus = isValidWindow(start, addMinutes(end, -STEP_MINUTES));
    const canEndPlus = isValidWindow(start, addMinutes(end, STEP_MINUTES));

    const duration = differenceInMinutes(end, start);
    const atMinimum = duration <= effectiveMinMinutes;
    const maxAllowedMinutes = maxMinutes ?? totalMinutes;
    const atMaximum = duration >= maxAllowedMinutes;
    const atBound =
      addMinutes(start, -STEP_MINUTES) < shiftStart ||
      addMinutes(end, STEP_MINUTES) > shiftEnd;

    let caption: string | null = null;
    if (atMinimum) {
      caption = i18next.t("flexi_custom_min_reached", {
        minHours: effectiveMinMinutes / 60,
      });
    } else if (atMaximum) {
      caption = i18next.t("flexi_custom_shift_hours", {
        start: format(shiftStart, "h:mm a"),
        end: format(shiftEnd, "h:mm a"),
      });
    } else if (atBound) {
      caption = i18next.t("flexi_custom_shift_hours", {
        start: format(shiftStart, "h:mm a"),
        end: format(shiftEnd, "h:mm a"),
      });
    }

    return { canStartMinus, canStartPlus, canEndMinus, canEndPlus, caption };
  }, [
    enabled,
    isValidWindow,
    start,
    end,
    effectiveMinMinutes,
    shiftStart,
    shiftEnd,
    totalMinutes,
    maxMinutes,
  ]);

  return { start, end, stepStart, stepEnd, ...derived };
}
